/**
 * DecksTab — how many people opened the decks we send out.
 *
 * `/deck` goes to investors, `/amenity` goes to building owners, and both are
 * sent one link at a time. The marketing site counts one visit per browser
 * session and this reads the totals back from `GET /api/views`.
 *
 * Every individual open is listed with its time, how far into the deck the
 * reader got, and how long they stayed — so "did they actually read it" is
 * answerable and not just "how many opened it".
 *
 * Sending `valetnetwork.co/deck?src=lerner` labels that link, so two people
 * sent the same deck can be told apart. Those labels are the "Who opened it"
 * list below.
 *
 * Same two caveats as the poster tab, for the same reasons:
 *  - A visit is somebody opening the page. It is not interest, and it is not
 *    a meeting.
 *  - The count endpoint is public and unauthenticated, so it is inflatable by
 *    anyone who cares to. Fine for "did they open it", not fine as an input to
 *    a decision about money.
 *
 * Link previews do not count. iMessage, Slack and LinkedIn fetch the page to
 * build their preview card and never run the counter, so pasting the link
 * somewhere does not show up here as a read.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { styled } from '@mui/system';
import { getDeckViews } from '../../services/adminService';
import TrackedLinkCard from './TrackedLinkCard';
import { recentDays, sumDays, whenAgo, shortDay } from './posterScanMath';
import { VALET, FONTS, TYPE } from '../../theme';

const DAYS_SHOWN = 14;
/** Matches the cap the endpoint applies. */
const HITS_SHOWN = 200;

/** What each deck is, in the words Rishi would use to describe it out loud. */
const DECKS = {
    deck: {
        name: 'Investor deck',
        url: 'valetnetwork.co/deck',
        blurb: 'The raise. Eleven slides, sent to investors.',
    },
    amenity: {
        name: 'Building deck',
        url: 'valetnetwork.co/amenity',
        blurb: 'Parking as an amenity. Sent to building owners.',
    },
};

/** Links here are made to be pasted into an email, so they carry the scheme. */
const SCHEME = 'https://';

/**
 * The label rule, copied from the marketing site rather than imported — this
 * console and valetnetwork.co are separate deploys with no shared code.
 *
 * Keep it identical to `SRC_RE` in `view-beacon.tsx` and `api/views/route.ts`.
 * A label that fails it is dropped *silently* on arrival: the visit still lands
 * in the totals, it just lands under nobody, and the link looks like it worked.
 * So the builder below will not hand over a link that would not survive.
 */
const SRC_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;

/** What the site does to a label before testing it — applied here as you type,
 *  so the box can never hold something that would be quietly discarded. */
const cleanLabel = (raw) => raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);

/** The thing you paste into the email. */
const buildLink = (page, label) => {
    const meta = DECKS[page];
    return `${SCHEME}${meta ? meta.url : `valetnetwork.co/${page}`}?src=${label}`;
};

/** "Tue, Sep 1 · 10:33 AM" — the clock Rishi is on, not UTC. */
const stamp = (iso) => {
    if (!iso) return 'unknown time';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'unknown time';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(d).replace(' at ', ' · ');
};

/** "4m 12s", or "12s". Nobody wants to read 252 seconds. */
const spell = (seconds) => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return rest ? `${m}m ${rest}s` : `${m}m`;
};

/**
 * "Read to slide 7 of 11 · 4m 12s".
 *
 * Empty when the browser never reported back — a reader who closes the tab
 * within a second of opening it, or one whose network dropped on the way out.
 * Shown as "opened, nothing more" rather than as a zero, because zero would
 * read as "they got nowhere" when the truth is we do not know.
 */
const depthOf = (h) => {
    const parts = [];
    if (h.maxSlide > 0) {
        parts.push(h.slides > 0 ? `Read to slide ${h.maxSlide} of ${h.slides}` : `Read to slide ${h.maxSlide}`);
    }
    const t = spell(h.seconds);
    if (t) parts.push(t);
    return parts.join(' · ');
};

/* ------------------------------------------------------------------ styles */

const SectionTitle = styled(Typography)({ ...TYPE.sectionTitle, marginBottom: '4px' });
const Blurb = styled(Typography)({ ...TYPE.bodySmall, color: VALET.textSecondary });

const Card = styled(Box)({
    backgroundColor: VALET.surface,
    borderRadius: '14px',
    border: `1px solid ${VALET.border}`,
    boxShadow: VALET.shadow,
    padding: '22px 24px',
    '@media (max-width: 768px)': {
        padding: '16px 14px',
    },
});

const KpiRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
});

const KpiLabel = styled(Typography)({ ...TYPE.kpiLabel, marginBottom: '10px' });
const KpiValue = styled(Typography)({ ...TYPE.kpiValue });
const KpiNote = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '12.5px',
    color: VALET.textMuted,
    marginTop: '6px',
});

// Deck · visits · last opened. Three columns rather than the poster tab's four
// — there are only ever two decks, so a share-of-total bar would compare a
// thing to itself.
const Row = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 2fr) 110px 140px',
    alignItems: 'center',
    gap: '18px',
    padding: '18px 0',
    borderBottom: `1px solid ${VALET.border}`,
    '&:last-of-type': { borderBottom: 'none' },
    '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
        alignItems: 'stretch',
        gap: '8px',
        padding: '16px 0',
    },
});

const HeadRow = styled(Row)({
    padding: '0 0 12px',
    borderBottom: `1px solid ${VALET.borderStrong || VALET.border}`,
    '@media (max-width: 768px)': {
        display: 'none',
    },
});

const HeadCell = styled(Typography)({ ...TYPE.kpiLabel, marginBottom: 0 });

const DeckName = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: VALET.textPrimary,
});

const Count = styled(Typography)({
    fontFamily: FONTS.display,
    fontSize: '24px',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: VALET.textPrimary,
    textAlign: 'right',
    '@media (max-width: 768px)': { textAlign: 'left' },
});

const Muted = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    color: VALET.textMuted,
});

const Tag = styled('span')({
    fontFamily: FONTS.mono,
    fontSize: '11px',
    color: VALET.bronze,
    backgroundColor: VALET.bgMuted,
    border: `1px solid ${VALET.bronzeHairline}`,
    borderRadius: '5px',
    padding: '2px 6px',
});

const SourceRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 0',
    borderBottom: `1px solid ${VALET.border}`,
    '&:last-of-type': { borderBottom: 'none' },
});

/* The builder sits inside the "Who opened it" card rather than in a card of
 * its own: making a labelled link and reading the labelled links you made are
 * the same job, and splitting them puts the answer two scrolls from the
 * question. */
const Builder = styled(Box)({
    marginTop: '16px',
    padding: '14px 16px',
    borderRadius: '10px',
    backgroundColor: VALET.surfaceMuted,
    border: `1px solid ${VALET.border}`,
});

const BuilderRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
});

const LinkOut = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginTop: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    backgroundColor: VALET.surface,
    border: `1px solid ${VALET.border}`,
});

const LinkText = styled(Typography)({
    fontFamily: FONTS.mono,
    fontSize: '13px',
    lineHeight: 1.5,
    minWidth: 0,
    overflowWrap: 'anywhere',
});

const CopyButton = styled(Button)({
    textTransform: 'none',
    fontFamily: FONTS.sans,
    fontSize: '13px',
    fontWeight: 600,
    color: VALET.green,
    whiteSpace: 'nowrap',
    minWidth: '68px',
    padding: '4px 10px',
});

/** MUI's selected state is specific enough that plain sx loses to it. */
const TOGGLE_SX = {
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        fontFamily: FONTS.sans,
        fontSize: '13px',
        fontWeight: 600,
        color: VALET.textSecondary,
        borderColor: VALET.border,
        padding: '6px 14px',
    },
    '& .MuiToggleButton-root.Mui-selected': {
        color: VALET.green,
        backgroundColor: VALET.greenLight,
        '&:hover': { backgroundColor: VALET.greenLight },
    },
};

const OpenRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '190px 1fr',
    alignItems: 'baseline',
    gap: '14px',
    padding: '9px 0',
    borderBottom: `1px solid ${VALET.border}`,
    '&:last-of-type': { borderBottom: 'none' },
    '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
        gap: '2px',
        padding: '10px 0',
    },
});

const When = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '14px',
    fontWeight: 600,
    color: VALET.textPrimary,
    whiteSpace: 'nowrap',
});

const Chart = styled(Box)({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    height: '120px',
    marginTop: '18px',
});

const ChartCol = styled(Box)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
});

const ChartBar = styled(Box)({
    width: '100%',
    borderRadius: '4px 4px 0 0',
    backgroundColor: VALET.green,
    minHeight: '2px',
});

const ChartLabel = styled(Typography)({
    fontFamily: FONTS.mono,
    fontSize: '9.5px',
    color: VALET.textMuted,
    whiteSpace: 'nowrap',
});

const Note = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    lineHeight: 1.6,
    color: VALET.textSecondary,
    marginTop: '18px',
});

const ErrorBox = styled(Typography)({
    color: VALET.danger,
    backgroundColor: VALET.dangerBg,
    fontSize: '13px',
    padding: '14px 18px',
    borderRadius: '8px',
    border: `1px solid ${VALET.danger}33`,
    fontWeight: 500,
    fontFamily: FONTS.sans,
});

/* --------------------------------------------------------------- component */

export default function DecksTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Builder state. Defaults to the building deck because that is the one that
    // goes out to a named person at a named building; the investor deck tends
    // to get forwarded, where a label stops meaning anything anyway.
    const [linkDeck, setLinkDeck] = useState('amenity');
    const [linkLabel, setLinkLabel] = useState('');
    // Which button last said "Copied", so two Copy buttons cannot both claim it.
    const [copied, setCopied] = useState('');
    const copiedTimer = useRef(null);

    useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

    const copy = useCallback((text, key) => {
        if (!text) return;

        // Only after the write actually lands: a button that says "Copied" over
        // an empty clipboard is worse than one that appears not to have fired.
        const confirm = () => {
            setCopied(key);
            window.clearTimeout(copiedTimer.current);
            copiedTimer.current = window.setTimeout(() => setCopied(''), 1600);
        };

        try {
            const done = navigator.clipboard.writeText(text);
            // Rejects rather than throws when the document is not focused or
            // permission is refused, so it needs both paths handled.
            if (done && typeof done.then === 'function') done.then(confirm, () => {});
            else confirm();
        } catch {
            // No clipboard at all — an old browser, or a page served over plain
            // http. Nothing useful to do: the link is on screen and can be
            // selected by hand, and rethrowing here would blank the tab.
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await getDeckViews());
        } catch (err) {
            setError(err.message || 'Could not load deck visits');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress sx={{ color: VALET.green }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <ErrorBox>{error}</ErrorBox>
                <Note>
                    The counts come from valetnetwork.co. If this keeps failing, the
                    marketing site is probably down or has not been deployed since deck
                    tracking was added.
                </Note>
                <Button onClick={load} sx={{ mt: 2, textTransform: 'none', color: VALET.green }}>
                    Try again
                </Button>
            </Box>
        );
    }

    const pages = data.pages || [];
    const hits = data.hits || [];
    const total = pages.reduce((n, p) => n + Number(p.count || 0), 0);
    const days = recentDays(DAYS_SHOWN);
    const lastWeekDays = days.slice(-7);
    const lastWeek = pages.reduce((n, p) => n + sumDays(p.days, lastWeekDays), 0);

    const perDay = days.map((day) => ({
        day,
        n: pages.reduce((n, p) => n + Number((p.days || {})[day] || 0), 0),
    }));
    // Never divide by zero, and never let a single visit draw a full-height bar.
    const peak = Math.max(1, ...perDay.map((d) => d.n));

    // Every labelled link across both decks, busiest first. This is the list
    // that answers "did the person I sent it to actually read it".
    const sources = pages
        .flatMap((p) =>
            Object.entries(p.sources || {}).map(([label, n]) => ({
                label,
                n: Number(n || 0),
                page: p.page,
            }))
        )
        .sort((a, b) => b.n - a.n);

    // Only opens that reported back can say anything about depth. A median
    // rather than a mean: one deck left open over lunch would drag an average
    // somewhere useless.
    const reported = hits.filter((h) => h.maxSlide > 0 || h.seconds > 0);
    const median = (nums) => {
        if (!nums.length) return 0;
        const a = [...nums].sort((x, y) => x - y);
        const mid = Math.floor(a.length / 2);
        return a.length % 2 ? a[mid] : Math.round((a[mid - 1] + a[mid]) / 2);
    };
    const medSlide = median(reported.map((h) => h.maxSlide));
    const medSecs = median(reported.map((h) => h.seconds));
    const typicalRead = reported.length === 0 ? '—' : `slide ${medSlide}`;
    const readNote =
        reported.length === 0
            ? 'Nothing reported back yet'
            : `Middle of ${reported.length} open${reported.length === 1 ? '' : 's'}${
                  medSecs ? `, ${spell(medSecs)} spent` : ''
              }`;

    const linkOk = SRC_RE.test(linkLabel);
    const link = linkOk ? buildLink(linkDeck, linkLabel) : '';
    const linkDeckMeta = DECKS[linkDeck];

    const lastAny = pages
        .map((p) => p.lastAt)
        .filter(Boolean)
        .sort()
        .pop();

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '20px' }}>
                <Box>
                    <SectionTitle>Deck visits</SectionTitle>
                    <Blurb>
                        How many people opened the decks we send out.
                    </Blurb>
                </Box>
                <Button onClick={load} sx={{ textTransform: 'none', color: VALET.green, fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <KpiRow>
                <Card>
                    <KpiLabel>Visits, all time</KpiLabel>
                    <KpiValue>{total.toLocaleString()}</KpiValue>
                    <KpiNote>Both decks added together</KpiNote>
                </Card>
                <Card>
                    <KpiLabel>Visits, last 7 days</KpiLabel>
                    <KpiValue>{lastWeek.toLocaleString()}</KpiValue>
                    <KpiNote>{lastWeek === 0 ? 'Nobody opened one this week' : 'Counted in New York time'}</KpiNote>
                </Card>
                <Card>
                    <KpiLabel>Typical read</KpiLabel>
                    <KpiValue sx={{ fontSize: '28px' }}>{typicalRead}</KpiValue>
                    <KpiNote>{readNote}</KpiNote>
                </Card>
                <Card>
                    <KpiLabel>Last opened</KpiLabel>
                    <KpiValue sx={{ fontSize: total === 0 ? undefined : '28px' }}>
                        {total === 0 ? '—' : whenAgo(lastAny)}
                    </KpiValue>
                    <KpiNote>{total === 0 ? 'No visits yet' : 'Either deck'}</KpiNote>
                </Card>
            </KpiRow>

            {/* The one link being followed visit by visit, slide by slide. */}
            <TrackedLinkCard copy={copy} copied={copied} />

            <Card>
                <HeadRow>
                    <HeadCell>Deck</HeadCell>
                    <HeadCell sx={{ textAlign: 'right' }}>Visits</HeadCell>
                    <HeadCell>Last opened</HeadCell>
                </HeadRow>

                {pages.map((p) => {
                    const meta = DECKS[p.page];
                    return (
                        <Row key={p.page}>
                            <Box sx={{ minWidth: 0 }}>
                                <DeckName>{meta ? meta.name : p.page}</DeckName>
                                <Muted sx={{ mt: '3px' }}>
                                    {meta ? meta.blurb : 'Added after this page was written'}
                                </Muted>
                                <Muted sx={{ mt: '3px', fontFamily: FONTS.mono, fontSize: '12px' }}>
                                    {meta ? meta.url : `valetnetwork.co/${p.page}`}
                                </Muted>
                            </Box>
                            <Count>{Number(p.count || 0).toLocaleString()}</Count>
                            <Muted>{whenAgo(p.lastAt)}</Muted>
                        </Row>
                    );
                })}
            </Card>

            <Card sx={{ mt: '20px' }}>
                <SectionTitle>Who opened it</SectionTitle>
                <Blurb>
                    Only links you labelled show up here. Send{' '}
                    <Tag>valetnetwork.co/deck?src=lerner</Tag> instead of the plain link
                    and that visit is counted under &ldquo;lerner&rdquo;.
                </Blurb>

                <Builder>
                    <KpiLabel>Make a labelled link</KpiLabel>
                    <BuilderRow>
                        <ToggleButtonGroup
                            value={linkDeck}
                            exclusive
                            size="small"
                            onChange={(_, v) => v && setLinkDeck(v)}
                            sx={TOGGLE_SX}
                        >
                            <ToggleButton value="deck">Investor</ToggleButton>
                            <ToggleButton value="amenity">Building</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            value={linkLabel}
                            onChange={(e) => setLinkLabel(cleanLabel(e.target.value))}
                            placeholder="extell"
                            size="small"
                            sx={{ width: 190 }}
                            inputProps={{
                                'aria-label': 'Label for this link',
                                spellCheck: false,
                                autoCapitalize: 'off',
                                autoCorrect: 'off',
                            }}
                            InputProps={{ sx: { fontFamily: FONTS.mono, fontSize: '14px' } }}
                        />
                    </BuilderRow>
                    <LinkOut>
                        <LinkText sx={{ color: linkOk ? VALET.textPrimary : VALET.textMuted }}>
                            {linkOk
                                ? link
                                : `${SCHEME}${linkDeckMeta.url}?src=`}
                        </LinkText>
                        <CopyButton
                            onClick={() => copy(link, 'builder')}
                            disabled={!linkOk}
                            sx={{ '&.Mui-disabled': { color: VALET.textMuted } }}
                        >
                            {copied === 'builder' ? 'Copied' : 'Copy'}
                        </CopyButton>
                    </LinkOut>
                    <Muted sx={{ mt: '8px', fontSize: '12.5px' }}>
                        Lowercase letters, numbers, dashes and underscores, up to 32,
                        starting with a letter or number. One label is one bucket — send
                        two people the same link and this page cannot tell them apart, so
                        use <Tag>extell-leasing</Tag> and <Tag>extell-ops</Tag> rather than
                        one label for a whole company.
                    </Muted>
                </Builder>

                <Box sx={{ mt: '18px' }}>
                    {sources.length === 0 ? (
                        <Muted>
                            No labelled links yet. Every visit so far came from a plain link,
                            so it counts in the totals above but cannot be traced to a person.
                        </Muted>
                    ) : (
                        sources.map((s) => {
                            const key = `src:${s.page}:${s.label}`;
                            return (
                                <SourceRow key={key}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <DeckName>{s.label}</DeckName>
                                        <Muted sx={{ mt: '2px' }}>
                                            {DECKS[s.page] ? DECKS[s.page].name : s.page}
                                        </Muted>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Muted sx={{ whiteSpace: 'nowrap' }}>
                                            {s.n.toLocaleString()} visit{s.n === 1 ? '' : 's'}
                                        </Muted>
                                        {/* Resending to the same person should not mean
                                            retyping the label and risking a typo, which
                                            would silently open a second bucket. */}
                                        <CopyButton onClick={() => copy(buildLink(s.page, s.label), key)}>
                                            {copied === key ? 'Copied' : 'Copy'}
                                        </CopyButton>
                                    </Box>
                                </SourceRow>
                            );
                        })
                    )}
                </Box>
            </Card>

            <Card sx={{ mt: '20px' }}>
                <SectionTitle>Every open</SectionTitle>
                <Blurb>
                    Newest first, New York time. The last {HITS_SHOWN} are kept.
                </Blurb>
                <Box sx={{ mt: '14px' }}>
                    {hits.length === 0 ? (
                        <Muted>Nothing opened yet.</Muted>
                    ) : (
                        hits.map((h, i) => (
                            // Two opens can share a millisecond, so the index is
                            // part of the key rather than the timestamp alone.
                            <OpenRow key={`${h.at}:${i}`}>
                                <When>{stamp(h.at)}</When>
                                <Box sx={{ minWidth: 0 }}>
                                    <Muted>
                                        {DECKS[h.page] ? DECKS[h.page].name : h.page}
                                        {h.src ? ` · ${h.src}` : ''}
                                    </Muted>
                                    <Muted sx={{ fontSize: '12.5px', opacity: 0.75 }}>
                                        {depthOf(h) || 'Opened — closed before it could report how far'}
                                    </Muted>
                                </Box>
                            </OpenRow>
                        ))
                    )}
                </Box>
            </Card>

            <Card sx={{ mt: '20px' }}>
                <SectionTitle>Visits a day, last {DAYS_SHOWN} days</SectionTitle>
                <Blurb>Both decks added together.</Blurb>
                <Chart>
                    {perDay.map(({ day, n }) => (
                        <ChartCol key={day}>
                            <Muted sx={{ fontSize: '11px' }}>{n || ''}</Muted>
                            <ChartBar sx={{ height: `${Math.round((n / peak) * 84)}%`, opacity: n ? 1 : 0.18 }} />
                            <ChartLabel>{shortDay(day)}</ChartLabel>
                        </ChartCol>
                    ))}
                </Chart>
            </Card>

            <Note>
                A visit means somebody opened the page. Counted once per person per
                visit, so rereading the same deck in the same tab only counts once.
                Link previews in iMessage, Slack and LinkedIn are not counted, so
                pasting the link somewhere never looks like a read.
                {total === 0 && ' Nothing has been opened yet. Open a deck yourself and hit Refresh — it should show 1.'}
            </Note>
        </Box>
    );
}
